// src/encounter/services/use-note-macros.ts
//
// The saved phrases (macros) the A/P narrative editor offers — every macro this provider may USE:
// their own, the org-wide (`isGlobal`) ones, and those shared with them as a collaborator.
//
//   GET /api/Doctor/GetMacros?organizationId={org}&providerId={id}&search=[&specialtyId=][&visitType=]
//
// SCOPE KEYS (TS-4, PROD-2820). The encounter passes the visit's specialty and appointment type code;
// PracticeManagement then hides phrases scoped to ANOTHER specialty or visit type and lists the ones
// scoped to this visit first. Unscoped phrases always show. With neither key the list is unchanged —
// Settings keeps calling without them. Both keys are in the query key, so a different visit refetches.
//
// IDENTITY (PROD-2811). PracticeManagement now derives the caller from the token (`sub` → setup id)
// and ignores `providerId` for authorisation and filtering; it is still sent for older servers.
//
// WHICH ENDPOINT. Flutter's encounter editor fetches exactly this (`quill_tool_bar.dart` `fetchList`
// → `GetMacrosEvent` → `settings_data_source_impl.dart:1453` `/GetMacros`), and the server query
// behind it (`MacroRepository.GetMacros`, Scrips.PracticeManagement) returns owner OR global OR
// collaborator rows. The Settings tab's `useMacrosByProviderId` hits `GetMacrosByProviderId`, which
// returns only macros the provider OWNS or collaborates on — right for managing them, wrong for using
// them (it would hide every org-wide phrase).
//
// WHICH ID. `providerId` is `user.id` — Flutter calls the bare `providerId()`, whose default returns
// `LoginUserData.id` (utils.dart:1232-1249), the same id `use-bundles.ts` documents. The org id rides
// both the query (Flutter parity) and the `OrganizationId` header the API client already sets (the
// controller binds it `[FromHeader]`).
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import { useAuthStore } from '@/auth/auth-store';
import type { MacroData } from '@/types/settings-api';
import { triggerOf, type NoteMacro } from '../lib/smart-phrase';

/** TS-4: the visit facts the phrase lookup is filtered by. */
export interface NoteMacroScope {
  specialtyId?: string;
  visitType?: string;
  /**
   * The visit facts are still being looked up (the page's GetById read). The lookup waits rather than
   * fetching an unfiltered list and swapping it a moment later (cross-model review, 2026-10-05).
   */
  pending?: boolean;
}

export function noteMacrosQueryKey(
  organizationId: string | undefined,
  providerId: string | undefined,
  scope: NoteMacroScope = {},
) {
  return ['encounter', 'note-macros', organizationId, providerId, scope.specialtyId ?? null, scope.visitType ?? null] as const;
}

/** Wire rows → the phrases the editor offers. Deleted rows and rows with no key are dropped (a
 *  phrase with no key can never be typed and has nothing to show in the list). Scope: the
 *  provider's own; otherwise org-wide when `isGlobal`; otherwise it reached them as a collaborator.
 *  (GetMacros returns no owner NAME — `SELECT *` over the Macro table — so duplicates are told
 *  apart by scope and preview, not by owner.) */
export function toNoteMacros(rows: MacroData[] | null | undefined, providerId?: string): NoteMacro[] {
  return (rows ?? [])
    .filter((m) => !m.isDeleted && m.macroKey?.trim())
    .map((m) => ({
      id: m.id,
      key: m.macroKey.trim(),
      trigger: triggerOf(m.macroKey),
      value: m.macroValue ?? '',
      preview: (m.macroValueFormatted ?? '').replace(/\s+/g, ' ').trim(),
      scope: providerId && m.ownerId === providerId ? 'own' : m.isGlobal ? 'global' : 'shared',
    }));
}

export function useNoteMacros(scope: NoteMacroScope = {}) {
  const organizationId = useAuthStore((s) => s.user?.organizationId) || undefined;
  const providerId = useAuthStore((s) => s.user?.id) || undefined;
  const specialtyId = scope.specialtyId?.trim() || undefined;
  const visitType = scope.visitType?.trim() || undefined;
  return useQuery({
    queryKey: noteMacrosQueryKey(organizationId, providerId, { specialtyId, visitType }),
    enabled: !!organizationId && !!providerId && scope.pending !== true,
    // Phrases change rarely and the dialog remounts on every open; a minute of reuse keeps reopening
    // the note from refetching every time without hiding a phrase saved in Settings for long.
    staleTime: 60_000,
    queryFn: async ({ signal }) => {
      const qs = new URLSearchParams({
        organizationId: organizationId as string,
        providerId: providerId as string,
        search: '',
      });
      if (specialtyId) qs.set('specialtyId', specialtyId);
      if (visitType) qs.set('visitType', visitType);
      return toNoteMacros(await get<MacroData[]>(`/api/Doctor/GetMacros?${qs.toString()}`, { signal }), providerId);
    },
  });
}
